import { Stepper, Step, StepLabel, Box, StepConnector, Alert, IconButton, Collapse } from "@mui/material";
import { useBrandRegistrationStatusAPI } from "./settings.api";
import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";
import { PATHS } from "src/routes";
import { useNavigate } from "react-router-dom";
import { useContext, useState } from "react";
import AppContext from "src/AppContext";
import { planSupportsMyFans } from "../constant";
import CloseIcon from '@mui/icons-material/Close';

var steps = [
  'Sign Up for ARELIA',
  'Subscribe to "My Fans"',
  'Register SMS Profile',
  'Pending Approval',
  'APPROVED (ready to send SMS)',
];

const stepExplanations = [
  "Welcome to ARELIA! Complete your sign-up to start connecting with your fans.",
  "To use SMS features, subscribe to the 'My Fans' plan. This enables you to connect directly with your audience.",
  "Register your SMS profile by providing the required information. This helps us set up your messaging capabilities.",
  "Your SMS profile is being reviewed. This typically takes 1-2 business weeks. We'll notify you once approved.",
  "Congratulations! You're now approved and ready to send SMS messages to your fans."
];

export type Props = {
  clickEnabled?: boolean;
  hideIfCompleted?: boolean;
};

export default function SettingsProcessSteps({ clickEnabled = false, hideIfCompleted = true }: Props) {
  const navigate = useNavigate();
  const { account } = useContext(AppContext);
  const { data: status, isLoading } = useBrandRegistrationStatusAPI(true, false);
  const [notificationHidden, setNotificationHidden] = useState(false);

  let activeStep = 1;
  if (planSupportsMyFans(account?.plan)) {
    if (!status?.started) {
      activeStep = 2; // Subscribed to "My Fans" (step 2)
    } else if (status?.phoneNumber) {
      activeStep = 4; // APPROVED (ready to send SMS) - final step completed
    } else if (status?.campaignVerified && status?.phoneNumber) {
      activeStep = 3; // Campaign approved and phone selected, but not fully completed yet
    } else if (status?.campaignVerified && !status?.phoneNumber) {
      activeStep = 3; // Campaign approved but waiting for phone number selection
    } else if (status?.brandCreated && !status?.brandOtpApproved) {
      activeStep = 3; // Created brand but waiting for OTP verification
    } else if (status?.brandOtpApproved && !status?.campaignVerified) {
      activeStep = 3; // OTP approved, waiting for campaign approval
    } else {
      activeStep = 3; // In progress with SMS registration
    }
  }

  const navigateBasedOnActiveStep = () => {
    if (!clickEnabled) return;

    if (activeStep === 1) {
      navigate(PATHS.SUBSCRIPTION);
    } else {
      navigate(PATHS.FANS_SETTINGS);
    }
  };

  const handleStepClick = (event: React.MouseEvent) => {
    if (!clickEnabled) return;

    event.stopPropagation();
    navigateBasedOnActiveStep();
  };

  const handleCloseNotification = () => {
    setNotificationHidden(true);
  };

  const showNotification = window.location.pathname === PATHS.FANS_DASHBOARD && !notificationHidden;

  return (hideIfCompleted && status?.completed) ? <></> : (
    <Box>
      <Box sx={styles.notificationContainer} onClick={(e) => e.stopPropagation()}>
        <Collapse in={showNotification}>
          <Alert
            severity="info"
            sx={styles.notificationBox}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleCloseNotification}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {stepExplanations[activeStep < stepExplanations.length ? activeStep : 0]}
          </Alert>
        </Collapse>
      </Box>

      <Box
        sx={styles.container(clickEnabled)}
        onClick={navigateBasedOnActiveStep}
        className="settings-process-steps"
      >
        <SpinnerWrapper loading={isLoading}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={<StepConnector sx={styles.connector} />}
          >
            {steps.map((label, index) => {
              const isActive = index === activeStep;
              const isLastStep = index === 4;
              const isCompleted = index < activeStep || (isLastStep && activeStep === 4);
              const isFuture = !isActive && !isCompleted;

              return (
                <Step
                  key={label}
                  active={isActive && !isLastStep}
                  completed={isCompleted}
                  sx={styles.step(clickEnabled)}
                  className={
                    (isActive && !isLastStep) ? "active-step" :
                      isCompleted ? "completed-step" : "future-step"
                  }
                  onClick={handleStepClick}
                >
                  <StepLabel
                    sx={styles.stepLabel(clickEnabled, isActive && !isLastStep, isFuture)}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </SpinnerWrapper>
      </Box>
    </Box>
  );
}

const styles = {
  container: (clickEnabled: boolean) => ({
    padding: '16px',
    borderRadius: '8px',
    cursor: clickEnabled ? 'pointer' : 'default',
    '& .MuiStepLabel-label': {
      fontWeight: 'normal',
    },
    '& .active-step': {
      transition: 'transform 0.2s ease, color 0.2s ease',
    },
    '&:hover': clickEnabled ? {
      '& .active-step .MuiStepLabel-root': {
        transform: 'translateY(-2px)',
        color: 'primary.main',
      },
      '& .active-step .MuiStepLabel-label': {
        color: 'primary.main',
      }
    } : {}
  }),
  connector: {
    transition: 'none',
    transform: 'none',
  },
  step: (clickEnabled: boolean) => ({
    transition: 'all 0.2s ease',
    cursor: clickEnabled ? 'pointer' : 'default',
    '&:hover': clickEnabled ? {
      '& .MuiStepLabel-root': {
        cursor: 'pointer',
      }
    } : {}
  }),
  stepLabel: (clickEnabled: boolean, isActive: boolean, isFuture: boolean) => ({
    cursor: clickEnabled ? 'pointer' : 'default',
    '& .MuiStepLabel-label': {
      cursor: clickEnabled ? 'pointer' : 'default',
      fontWeight: 'normal',
      color: isFuture ? 'text.disabled' : 'text.primary',
    },
    '& .MuiStepIcon-root': {
      cursor: clickEnabled ? 'pointer' : 'default',
      color: isFuture ? 'action.disabled' : undefined,
    },
    '& .MuiStepIcon-root.Mui-active': {
      color: 'primary.main',
    }
  }),
  notificationBox: {
    marginTop: 2,
    marginBottom: 2,
    backgroundColor: 'rgb(229, 246, 253)',
    borderRadius: '8px',
    '& .MuiAlert-message': {
      color: 'rgb(1, 67, 97)',
      fontWeight: 500,
      fontSize: '0.95rem',
    },
    '& .MuiAlert-icon': {
      color: 'rgb(3, 122, 171)'
    }
  },
  notificationContainer: {
    cursor: 'default'
  }
};
